import React from 'react';
import Grid from '@mui/material/Grid2';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import useLocalization from 'hooks/useLocalization';
import {
  MenuSingleTemplatePredefinedParameter,
  MenuSingleTemplatePropsWithPredefinedParams,
  TemplateVarsData,
  TemplateVarsDataItem,
  TemplateVarsProps,
} from 'types/templates/templates-menu';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import FilePresentOutlinedIcon from '@mui/icons-material/FilePresentOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ViewColumnOutlinedIcon from '@mui/icons-material/ViewColumnOutlined';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import CollectionsOutlinedIcon from '@mui/icons-material/CollectionsOutlined';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import { VisuallyHiddenInput } from 'ui-component/system/VisuallyHiddenInput';
import { clickPreventDefaultAndStopPropagation } from 'utils/helpers';
import { MediaFile, MediaFileType } from 'types/files';
import { useDebouncedCallback } from 'use-debounce';
import SelectFromGalleryDialog from 'ui-component/display/SelectFromGalleryDialog';
import UniformParamValueNoticeDialog from 'views/common/users-params-mapper/UniformParamValueNoticeDialog';
import { BROADCAST_UNIFORM_PARAM_NOTICE_DISMISSED_KEY } from 'constants/fireberryMetadata';
import { getHeaderMediaFieldLabel } from './template-media-field-label';
import TemplateParamMediaBadge from './TemplateParamMediaBadge';
import InfoTooltipTrigger from 'ui-component/help/InfoTooltipTrigger';
import { lightHelpTooltipSlotProps } from 'ui-component/help/lightHelpTooltipSlotProps';

interface VariableInputProps {
  param: TemplateVarsDataItem;
  templateMediaLoading?: boolean;
  uploadTemplateFile: (e: React.ChangeEvent<HTMLInputElement>) => Promise<{
    Name: string;
    Link: any;
  } | null>;
  predefinedParamsList: Array<MenuSingleTemplatePredefinedParameter>;
  predefinedChangeAction: MenuSingleTemplatePropsWithPredefinedParams['predefinedChangeAction'];
  predefinedSelectedColumns?: TemplateVarsProps['predefinedSelectedColumns'];
  index: number;
  handleTemplateComponentTextParam: (
    paramName: TemplateVarsData[0]['name'],
    value: string,
    paramIndex?: number,
  ) => void;
  predefinedExampleData: MenuSingleTemplatePropsWithPredefinedParams['predefinedExampleData'];
  setFileName: (fileName: string) => void;
  broadcastWizardParams?: boolean;
}

function truncateDisplay(value: string, max = 42): string {
  if (value.length <= max) return value;
  return `${value.slice(0, max)}…`;
}

const VariableInput: React.FC<VariableInputProps> = ({
  param,
  templateMediaLoading,
  uploadTemplateFile,
  predefinedParamsList,
  predefinedChangeAction,
  predefinedSelectedColumns,
  index,
  handleTemplateComponentTextParam,
  predefinedExampleData,
  setFileName,
  broadcastWizardParams = false,
}) => {
  const { currentLocale } = useLocalization();
  const localization = React.useMemo(() => currentLocale.templatesMenu, [currentLocale]);
  const uniformParamLoc = React.useMemo(
    () => currentLocale.settingsPopup.massMessagingTasks.taskUsersTable.uniformParamMode,
    [currentLocale],
  );
  const previewLoc = React.useMemo(
    () => currentLocale.settingsPopup.massMessagingTasks.taskUsersTable.templatePreview,
    [currentLocale],
  );
  const tableLoc = React.useMemo(
    () => currentLocale.settingsPopup.massMessagingTasks.taskUsersTable,
    [currentLocale],
  );

  const dataParamLabel = React.useMemo(
    () => getHeaderMediaFieldLabel(param, localization.singleTemplate.media),
    [param, localization.singleTemplate.media],
  );

  const isMediaType = React.useMemo(() => param.name === 'data', [param.name]);
  const colKey = React.useMemo(() => `var${index + 1}`, [index]);
  const paramLabel =
    param.name === 'data' ? dataParamLabel : `{{${param.name.replaceAll(/\D/g, '')}}}`;

  const mappedColumn = React.useMemo(
    () => predefinedSelectedColumns?.find((col) => col.key === colKey),
    [colKey, predefinedSelectedColumns],
  );

  const [value, setValue] = React.useState(mappedColumn?.selected ?? '');
  const [parameterType, setParameterType] = React.useState<'column' | 'value'>(mappedColumn?.type ?? 'column');
  const [chooseFromGallery, setChooseFromGallery] = React.useState<MediaFileType | null>(null);
  const [showUniformNoticeDialog, setShowUniformNoticeDialog] = React.useState(false);
  const [uniformNoticeDontShowAgain, setUniformNoticeDontShowAgain] = React.useState(false);
  const [showLinkInput, setShowLinkInput] = React.useState(false);

  React.useEffect(() => {
    if (!mappedColumn) return;
    const nextType = mappedColumn.type ?? 'column';
    const nextValue = mappedColumn.selected ?? '';
    setParameterType(nextType);
    setValue(nextValue);
    if (nextType === 'value' && nextValue.includes('://')) {
      setShowLinkInput(true);
    }
    handleTemplateComponentTextParam(
      param.name,
      nextType === 'value' ? nextValue : predefinedExampleData[nextValue] ?? '',
      index,
    );
  }, [
    handleTemplateComponentTextParam,
    index,
    mappedColumn?.key,
    mappedColumn?.selected,
    mappedColumn?.type,
    param.name,
    predefinedExampleData,
  ]);

  const isUniformNoticeDismissed = React.useCallback((): boolean => {
    try {
      return localStorage.getItem(BROADCAST_UNIFORM_PARAM_NOTICE_DISMISSED_KEY) === '1';
    } catch {
      return false;
    }
  }, []);

  const activateUniformValueMode = React.useCallback(() => {
    setValue('');
    setShowLinkInput(false);
    setParameterType('value');
    predefinedChangeAction(null, colKey, 'value');
    handleTemplateComponentTextParam(param.name, '', index);
  }, [colKey, handleTemplateComponentTextParam, index, param.name, predefinedChangeAction]);

  const activateColumnMode = React.useCallback(() => {
    setValue('');
    setShowLinkInput(false);
    setParameterType('column');
    predefinedChangeAction(null, colKey, 'column');
    handleTemplateComponentTextParam(param.name, '', index);
  }, [colKey, handleTemplateComponentTextParam, index, param.name, predefinedChangeAction]);

  const handleConfirmUniformNotice = React.useCallback(() => {
    if (uniformNoticeDontShowAgain) {
      try {
        localStorage.setItem(BROADCAST_UNIFORM_PARAM_NOTICE_DISMISSED_KEY, '1');
      } catch {
        /* ignore */
      }
    }
    activateUniformValueMode();
    setShowUniformNoticeDialog(false);
    setUniformNoticeDontShowAgain(false);
  }, [activateUniformValueMode, uniformNoticeDontShowAgain]);

  const handleUniformNoticeDialogClose = React.useCallback(
    (_: object, reason: 'backdropClick' | 'escapeKeyDown') => {
      if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
        setShowUniformNoticeDialog(false);
        setUniformNoticeDontShowAgain(false);
      }
    },
    [],
  );

  const requestUniformValueMode = React.useCallback(() => {
    if (isUniformNoticeDismissed()) {
      activateUniformValueMode();
    } else {
      setShowUniformNoticeDialog(true);
    }
  }, [activateUniformValueMode, isUniformNoticeDismissed]);

  const handleModeChange = React.useCallback(
    (_: React.MouseEvent<HTMLElement>, next: 'column' | 'value' | null) => {
      if (!next || next === parameterType) return;
      if (next === 'value') {
        requestUniformValueMode();
        return;
      }
      activateColumnMode();
    },
    [activateColumnMode, parameterType, requestUniformValueMode],
  );

  const toggleParameterType = () => {
    if (parameterType === 'column') {
      requestUniformValueMode();
      return;
    }
    activateColumnMode();
  };

  const debouncedOnChange = useDebouncedCallback(predefinedChangeAction, 300);

  const handleValue = (newValue: string, type: 'column' | 'value', debounce: boolean = true) => {
    setValue(newValue);
    if (debounce) debouncedOnChange(newValue, colKey, type);
    else predefinedChangeAction(newValue, colKey, type);

    handleTemplateComponentTextParam(
      param.name,
      type === 'value' ? newValue : predefinedExampleData[newValue],
      index,
    );
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const fileData = await uploadTemplateFile(e);
    if (fileData) {
      handleValue(fileData.Link, 'value', false);
      setFileName(fileData.Name);
      setShowLinkInput(false);
    }
  };

  const onSelectFileFromGallery = (file: MediaFile) => {
    handleValue(file.Link, 'value', false);
    handleTemplateComponentTextParam('data', file.Link);
    setFileName(file.Name);
    setChooseFromGallery(null);
    setShowLinkInput(false);
  };

  const clearUniformFile = () => {
    setValue('');
    setShowLinkInput(false);
    predefinedChangeAction(null, colKey, 'value');
    handleTemplateComponentTextParam(param.name, '', index);
    setFileName('');
  };

  const toggleTooltip =
    parameterType === 'column'
      ? isMediaType
        ? previewLoc.switchToFileUpload
        : uniformParamLoc.switchToManual
      : uniformParamLoc.switchToColumn;

  const hasMappedFileLink = isMediaType && parameterType === 'value' && Boolean(value);

  const paramIndexLabel = `{{${index + 1}}}`;

  const broadcastHeaderTitle = isMediaType ? (
    <>
      <TemplateParamMediaBadge param={param} compact />
      <Typography
        variant="caption"
        component="span"
        sx={{
          fontWeight: 600,
          color: 'text.secondary',
          fontFamily: 'monospace',
          fontSize: '0.7rem',
          lineHeight: 1.2,
          flexShrink: 0,
        }}
      >
        {paramIndexLabel}
      </Typography>
    </>
  ) : (
    <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.primary' }}>
      {paramLabel}
    </Typography>
  );

  const broadcastHint = React.useMemo(() => {
    if (parameterType === 'value') {
      return isMediaType ? previewLoc.fileParamHelper : uniformParamLoc.uniformValueHint;
    }
    return isMediaType ? previewLoc.fileColumnHint : tableLoc.columnMappingTooltip;
  }, [
    isMediaType,
    parameterType,
    previewLoc.fileColumnHint,
    previewLoc.fileParamHelper,
    tableLoc.columnMappingTooltip,
    uniformParamLoc.uniformValueHint,
  ]);

  const broadcastModeToggle = broadcastWizardParams ? (
    <ToggleButtonGroup
      exclusive
      size="small"
      value={parameterType}
      onChange={handleModeChange}
      sx={{
        flexShrink: 0,
        '& .MuiToggleButton-root': {
          py: 0.2,
          px: 0.85,
          textTransform: 'none',
          fontSize: '0.6875rem',
          lineHeight: 1.25,
          whiteSpace: 'nowrap',
        },
      }}
    >
      <ToggleButton value="column">{previewLoc.fromColumn}</ToggleButton>
      <ToggleButton value="value">
        {isMediaType ? previewLoc.uniformFile : previewLoc.uniformValue}
      </ToggleButton>
    </ToggleButtonGroup>
  ) : null;

  const broadcastFieldInputSx = {
    backgroundColor: 'background.paper',
    borderRadius: 1,
    '& .MuiSelect-select': { py: 0.875, px: 1.25 },
    '& .MuiInputBase-input': { py: 0.875, px: 1.25 },
  } as const;

  const broadcastColumnField = (
    <FormControl fullWidth size="small" error={!value}>
      <Select
        displayEmpty
        value={value}
        onChange={(e) => handleValue(e.target.value, 'column', false)}
        sx={broadcastFieldInputSx}
        renderValue={(selected) =>
          selected ? String(selected) : tableLoc.columnSearchPlaceholder
        }
      >
        {predefinedParamsList.map((option) => (
          <MenuItem value={option} key={option}>
            {option}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  const columnField = (
    <FormControl fullWidth size="small" error={!value}>
      <InputLabel id={`${param.label}-label-${index}`}>{paramLabel}</InputLabel>
      <Select
        labelId={`${param.label}-label-${index}`}
        id={`${param.label}-${index}`}
        value={value}
        label={paramLabel}
        onChange={(e) => handleValue(e.target.value, 'column', false)}
        sx={{ backgroundColor: 'background.paper' }}
      >
        {predefinedParamsList.map((option) => (
          <MenuItem value={option} key={option}>
            {option}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  const textValueField = (
    <TextField
      size="small"
      fullWidth
      sx={broadcastWizardParams ? broadcastFieldInputSx : { backgroundColor: 'background.paper' }}
      value={value}
      error={!value}
      placeholder={uniformParamLoc.enterValue}
      helperText={broadcastWizardParams || !value ? undefined : uniformParamLoc.uniformValueHint}
      label={broadcastWizardParams ? undefined : paramLabel}
      onChange={(e) => handleValue(e.target.value, 'value')}
      onBlur={(e) => {
        debouncedOnChange.cancel();
        predefinedChangeAction(e.target.value, colKey, 'value');
      }}
      slotProps={{
        htmlInput: { dir: 'rtl', style: { textAlign: 'start' } },
      }}
    />
  );

  const mediaValueField = hasMappedFileLink ? (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1}
      sx={{
        p: broadcastWizardParams ? 0.75 : 1,
        borderRadius: 1,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
      }}
    >
      <FilePresentOutlinedIcon color="info" fontSize="small" />
      <Typography variant="body2" noWrap sx={{ flex: 1, minWidth: 0 }}>
        {truncateDisplay(value)}
      </Typography>
      <IconButton size="small" onClick={clearUniformFile} aria-label={previewLoc.clearFile}>
        <CloseOutlinedIcon fontSize="small" />
      </IconButton>
    </Stack>
  ) : (
    <Stack spacing={broadcastWizardParams ? 0.5 : 1}>
      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
        <Button
          variant="outlined"
          size="small"
          component="label"
          disabled={templateMediaLoading}
          startIcon={
            templateMediaLoading ? <CircularProgress size={16} color="inherit" /> : <FileUploadOutlinedIcon />
          }
        >
          {previewLoc.uploadFile}
          <VisuallyHiddenInput
            type="file"
            accept={
              param.label === 'image'
                ? 'image/*'
                : param.label === 'document'
                  ? '.docx,.doc, .sls,.xlsx,.pdf,.zip'
                  : param.label === 'video'
                    ? 'video/*'
                    : undefined
            }
            onChange={handleFileUpload}
            id={`template-header-media-upload-${index}`}
          />
        </Button>
        <Button
          variant="outlined"
          size="small"
          startIcon={<CollectionsOutlinedIcon />}
          disabled={templateMediaLoading}
          onClick={(e) => {
            clickPreventDefaultAndStopPropagation(e);
            setChooseFromGallery(param.label as MediaFileType);
          }}
        >
          {previewLoc.chooseFromGallery}
        </Button>
      </Stack>
      {showLinkInput ? (
        <TextField
          size="small"
          fullWidth
          value={value}
          error={!value}
          placeholder={previewLoc.fileUrlPlaceholder}
          onChange={(e) => handleValue(e.target.value, 'value')}
          onBlur={(e) => {
            debouncedOnChange.cancel();
            predefinedChangeAction(e.target.value, colKey, 'value');
          }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <LinkOutlinedIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
            },
            htmlInput: { dir: 'ltr', style: { textAlign: 'start' } },
          }}
        />
      ) : (
        <Button
          size="small"
          variant="text"
          onClick={() => setShowLinkInput(true)}
          sx={{ alignSelf: 'flex-start', px: 0.5, minWidth: 0 }}
        >
          {previewLoc.pasteLink}
        </Button>
      )}
    </Stack>
  );

  if (broadcastWizardParams) {
    return (
      <>
        <Stack spacing={0.5} sx={{ width: '100%' }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={1}
            sx={{ width: '100%', minHeight: 28, gap: 1 }}
          >
            <Stack
              direction="row"
              alignItems="center"
              spacing={0.5}
              sx={{ minWidth: 0, flex: '1 1 auto' }}
            >
              {broadcastHeaderTitle}
              <InfoTooltipTrigger
                tooltipTitle={broadcastHint}
                ariaLabel={broadcastHint}
                placement="top"
                enterDelay={300}
                size="small"
                tabIndex={-1}
                tooltipSlotProps={lightHelpTooltipSlotProps}
                sx={{ p: 0.15 }}
              />
            </Stack>
            {broadcastModeToggle}
          </Stack>
          <Box sx={{ px: 0.75, pt: 0.25 }}>
            {parameterType === 'column'
              ? broadcastColumnField
              : isMediaType
                ? mediaValueField
                : textValueField}
          </Box>
        </Stack>

        {param.name === 'data' && (
          <SelectFromGalleryDialog
            open={Boolean(chooseFromGallery)}
            mediaType={chooseFromGallery ?? 'image'}
            onClose={() => setChooseFromGallery(null)}
            onSelectFile={onSelectFileFromGallery}
          />
        )}

        <UniformParamValueNoticeDialog
          open={showUniformNoticeDialog}
          dontShowAgain={uniformNoticeDontShowAgain}
          onDontShowAgainChange={setUniformNoticeDontShowAgain}
          onConfirm={handleConfirmUniformNotice}
          onClose={handleUniformNoticeDialogClose}
        />
      </>
    );
  }

  return (
    <>
      <Grid container spacing={1.5} alignItems={'flex-start'}>
        <Grid size="grow">
          {parameterType === 'value' ? (
            <Stack spacing={0.75}>
              {isMediaType ? <TemplateParamMediaBadge param={param} /> : null}
              <TextField
                size="small"
                fullWidth
                sx={{ backgroundColor: 'background.paper' }}
                value={value}
                error={!value}
                placeholder={isMediaType ? previewLoc.fileUrlPlaceholder : uniformParamLoc.enterValue}
                helperText={
                  isMediaType
                    ? previewLoc.fileParamHelper
                    : value
                      ? uniformParamLoc.uniformValueHint
                      : undefined
                }
                onChange={(e) => handleValue(e.target.value, 'value')}
                onBlur={(e) => {
                  debouncedOnChange.cancel();
                  predefinedChangeAction(e.target.value, colKey, 'value');
                }}
                slotProps={{
                  input: {
                    startAdornment: hasMappedFileLink ? (
                      <InputAdornment position="start">
                        <LinkOutlinedIcon fontSize="small" color="info" />
                      </InputAdornment>
                    ) : undefined,
                    endAdornment:
                      param.name === 'data' ? (
                        <InputAdornment position="end">
                          {templateMediaLoading ? (
                            <CircularProgress size={20} color="secondary" />
                          ) : (
                            <React.Fragment>
                              <IconButton
                                size="small"
                                component="label"
                                aria-label={localization.singleTemplate.uploadFileOrUseLink}
                              >
                                <FileUploadOutlinedIcon />
                                <VisuallyHiddenInput
                                  type="file"
                                  accept={
                                    param.label === 'image'
                                      ? 'image/*'
                                      : param.label === 'document'
                                        ? '.docx,.doc, .sls,.xlsx,.pdf,.zip'
                                        : param.label === 'video'
                                          ? 'video/*'
                                          : undefined
                                  }
                                  onChange={handleFileUpload}
                                  id={`template-header-media-upload-${index}`}
                                />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  clickPreventDefaultAndStopPropagation(e);
                                  setChooseFromGallery(param.label as MediaFileType);
                                }}
                                aria-label={previewLoc.chooseFromGallery}
                              >
                                <CollectionsOutlinedIcon />
                              </IconButton>
                            </React.Fragment>
                          )}
                        </InputAdornment>
                      ) : undefined,
                  },
                }}
                label={paramLabel}
              />
              {hasMappedFileLink ? (
                <Chip
                  size="small"
                  color="info"
                  variant="outlined"
                  icon={<FilePresentOutlinedIcon />}
                  label={previewLoc.fileMappedChip}
                  sx={{ alignSelf: 'flex-start', maxWidth: '100%' }}
                />
              ) : null}
            </Stack>
          ) : (
            <Stack spacing={0.75}>
              {isMediaType ? (
                <Box>
                  <TemplateParamMediaBadge param={param} />
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    {previewLoc.fileColumnHint}
                  </Typography>
                </Box>
              ) : null}
              {columnField}
            </Stack>
          )}
        </Grid>
        <Grid>
          <Tooltip title={toggleTooltip}>
            <IconButton
              onClick={() => toggleParameterType()}
              aria-label={toggleTooltip}
              sx={{ mt: isMediaType && parameterType === 'column' ? 3.5 : 0.5 }}
            >
              {parameterType === 'column' ? (
                isMediaType ? (
                  <FilePresentOutlinedIcon />
                ) : (
                  <EditOutlinedIcon />
                )
              ) : (
                <ViewColumnOutlinedIcon />
              )}
            </IconButton>
          </Tooltip>
        </Grid>

        {param.name === 'data' && (
          <SelectFromGalleryDialog
            open={Boolean(chooseFromGallery)}
            mediaType={chooseFromGallery ?? 'image'}
            onClose={() => setChooseFromGallery(null)}
            onSelectFile={onSelectFileFromGallery}
          />
        )}
      </Grid>

      <UniformParamValueNoticeDialog
        open={showUniformNoticeDialog}
        dontShowAgain={uniformNoticeDontShowAgain}
        onDontShowAgainChange={setUniformNoticeDontShowAgain}
        onConfirm={handleConfirmUniformNotice}
        onClose={handleUniformNoticeDialogClose}
      />
    </>
  );
};

export default VariableInput;
